"use client";

import { SelectRadioItemType } from "@/types/ui";
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { scrollCommandListOnWheel } from "@lib/ui";
import { cn } from "@lib/utils";

interface ComboboxSingleProps {
  items?: SelectRadioItemType[];
  /** Selected option `value` (e.g. event id as string). */
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Searchable single-select combobox (same interaction model as {@link Combobox}, one value only).
 */
export const ComboboxSingle = ({
  items = [],
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
}: ComboboxSingleProps) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const tCommon = useTranslations("common");

  const selectedItem = value ? items.find((c) => c.value === value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("flex w-full gap-1", className)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-9 min-w-0 flex-1 justify-between px-2.5 font-normal"
            disabled={disabled}
          >
            <span className="truncate text-left">
              {selectedItem ? (
                selectedItem.label
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground/80" aria-hidden />
          </Button>
        </PopoverTrigger>
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 shrink-0"
            disabled={disabled}
            onClick={() => onValueChange?.("")}
            aria-label={tCommon("words.verbs.reset")}
          >
            <XIcon className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      <PopoverContent className="w-(--radix-popper-anchor-width) p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList
            className="max-h-50 overscroll-contain"
            onWheel={scrollCommandListOnWheel}
          >
            <CommandEmpty>{tCommon("words.nouns.no-data")}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  keywords={[item.label]}
                  onSelect={() => {
                    onValueChange?.(item.value);
                    setOpen(false);
                  }}
                >
                  <span className="truncate">{item.label}</span>
                  {value === item.value && <CheckIcon className="ml-auto size-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
